import { RouteAndTeamContext } from "@/components/context-providers/RouteAndTeamInfoProvider";
import { Icon, type IconName } from "@/components/icons/Icon";
import { MenuSectionV2 } from "@/components/interactive/MenuContainerV2";
import { SwitchV2 } from "@/components/interactive/SwitchV2";
import { useImpersonatedTeamId } from "@/utils/hooks/use-is-impersonating";
import { useSession } from "next-auth/react";
import { useContext, type FC } from "react";

export const ProjectNavbarAdminDiagnostics: FC<{ adminMode: boolean }> = ({ adminMode }) => {
  const { data: session } = useSession();
  const routeAndTeamInfo = useContext(RouteAndTeamContext);
  const impersonatedTeamId = useImpersonatedTeamId();
  const isImpersonating = Boolean(impersonatedTeamId);
  const homeTeamId = session?.teamId;

  return (
    <MenuSectionV2 aria-label="User diagnostics">
      <DiagnosticBoolean label="Admin mode enabled" checked={adminMode} icon="wrench" />
      <DiagnosticBoolean label="Impersonating" checked={isImpersonating} icon="personalization" />
      <dl className="px-cds-8 text-cds-sm text-cds-ink dark:text-zinc-300">
        {isImpersonating && (
          <DiagnosticTeam label="Impersonated team" teamId={impersonatedTeamId} />
        )}
        <DiagnosticTeam label="Your team" teamId={homeTeamId} />
      </dl>
      {isImpersonating && impersonatedTeamId === homeTeamId && (
        <DiagnosticBoolean label="Impersonating your own team" checked />
      )}
      <dl className="px-cds-8 text-cds-sm text-cds-ink dark:text-zinc-300">
        <DiagnosticTeam label="Viewing data for team" teamId={routeAndTeamInfo?.team?.id} />
      </dl>
    </MenuSectionV2>
  );
};

const DiagnosticBoolean: FC<{ label: string; checked: boolean; icon?: IconName }> = ({
  label,
  checked,
  icon,
}) => (
  <div className="flex min-h-7 items-center justify-between gap-cds-8 px-cds-8 text-cds-sm text-cds-ink dark:text-zinc-300">
    <span className="flex items-center gap-cds-4">
      <span>{label}</span>
      {icon != null && (
        <Icon
          name={icon}
          size="md"
          aria-hidden
          data-testid={`admin-diagnostic-label-icon-${icon}`}
        />
      )}
    </span>
    <SwitchV2 aria-label={label} checked={checked} disabled />
  </div>
);

const DiagnosticTeam: FC<{ label: string; teamId: string | null | undefined }> = ({
  label,
  teamId,
}) => (
  <div className="flex min-h-7 items-baseline justify-between gap-cds-8 py-cds-4">
    <dt className="shrink-0">{label}:</dt>
    <dd className="min-w-0 break-all text-right font-mono">{teamId ?? "Unavailable"}</dd>
  </div>
);
